import { useMemo, useState } from 'react';

import { useAgentId, useAuth } from '../lib/use-auth.js';
import { useI18n } from '../lib/use-i18n.js';
import { usePlatformBrand } from '../lib/use-platform-brand.js';
import { useTrackedSnapshot } from '../lib/use-tracked-snapshot.js';
import { earningsLedgerRepoFor } from '../lib/repositories/earnings-repo.js';
import { downloadCsv } from '../lib/csv-download.js';
import {
  EarningsControls,
  csvDateStamp,
  earningsCsvLabels,
} from '../components/earnings-controls.js';
import {
  classifyLedgerEntry,
  filterLedgerByRange,
  filterLedgerByStatus,
  money,
  summariseLedgerTotals,
  toEarningsStatementCsv,
  type EarningsLedgerEntry,
  type LedgerEntryClass,
  type LedgerRange,
  type LedgerStatus,
} from '@kilat/shared';

/**
 * /earnings — every accrual, clawback, adjustment, and payout in one
 * chronological feed, grouped by month. Reads
 * `agents/{agentId}/earningsLedger/{entryId}` (written by `commissionAccrue`
 * in Sprint 37, and later by clawback + payout-run Functions).
 *
 * Per [feedback-no-dead-ends]: every state ends in a clear next action
 *   - signed-out          → "Sign in" CTA (Phase 1+ — placeholder for now)
 *   - signed-in but not an agent → explicit message + link back to marketing
 *   - loading             → skeleton hero + skeleton rows
 *   - error               → message + Retry button
 *   - empty               → friendly empty state explaining what triggers an entry
 *   - populated           → hero totals + range filter + monthly groups
 *
 * The page never reads `tenants/{tid}` directly (security rules forbid
 * cross-tenant reads). Operator names come from the `tenantBusinessName`
 * field that `commissionAccrue` snapshots into `basis` on each accrual.
 *
 * Filters + CSV (this task): a status filter (all / unpaid / paid-out,
 * derived from `entry.payoutId` presence) alongside the range filter, and an
 * "Export CSV" button over the CURRENTLY-FILTERED entries. The range defaults
 * to all-time so an agent whose accruals landed in earlier months sees their
 * real earnings, not an empty "this month" view. All money math flows through
 * the shared, tested `summariseLedgerTotals` / `filterLedger*` helpers.
 */

export function EarningsPage() {
  const { user, loading: authLoading } = useAuth();
  const agentId = useAgentId();
  const t = useI18n();
  const brand = usePlatformBrand();
  const [range, setRange] = useState<LedgerRange>('all_time');
  const [status, setStatus] = useState<LedgerStatus>('all');
  // Listener-level failure (permission / network / quota) surfaced from the
  // repo's onError, plus a retry nonce that re-subscribes. Without onError a
  // dropped read never fires the callback, so `entriesLoading` stayed true
  // forever and the error branch below was unreachable dead code — a permanent
  // skeleton with no escape (per [[feedback-no-dead-ends]]). Mirrors #65/#66.
  const [loadError, setLoadError] = useState<Error | null>(null);
  const [retryNonce, setRetryNonce] = useState(0);

  const subscribe = useMemo(() => {
    if (!agentId) return null;
    const repo = earningsLedgerRepoFor(agentId);
    return (cb: (vs: EarningsLedgerEntry[]) => void) =>
      repo.listSnapshot(
        (v) => {
          setLoadError(null);
          cb(v);
        },
        (err) => setLoadError(err),
      );
  }, [agentId]);

  const { data: entries, loading: entriesLoading } = useTrackedSnapshot<EarningsLedgerEntry[]>(
    ['earnings', agentId, retryNonce],
    subscribe,
  );

  // ── states ──────────────────────────────────────────────────────────
  if (authLoading) {
    return (
      <PageShell>
        <HeroSkeleton />
      </PageShell>
    );
  }

  if (!user) {
    return (
      <PageShell>
        <EmptyState
          title={t.t('agent.payouts.signedOutTitle')}
          body={t.t('agent.earnings.signedOutBody')}
          ctaLabel={t.t('agent.payouts.signInCta')}
          ctaHref="/"
          ctaNote={t.t('agent.payouts.signInNote')}
        />
      </PageShell>
    );
  }

  if (!agentId) {
    return (
      <PageShell>
        <EmptyState
          title={t.t('agent.payouts.notAgentTitle')}
          body={t.t('agent.earnings.notAgentBody')}
          ctaLabel={t.t('agent.payouts.notAgentCta')}
          ctaHref={`https://${brand.domains.marketing}/become-an-agent`}
        />
      </PageShell>
    );
  }

  if (loadError) {
    return (
      <PageShell>
        <EmptyState
          title={t.t('agent.loadError.earningsTitle')}
          body={t.t('agent.loadError.body')}
          onRetry={() => {
            setLoadError(null);
            setRetryNonce((n) => n + 1);
          }}
          retryLabel={t.t('agent.loadError.retry')}
        />
      </PageShell>
    );
  }

  if (entriesLoading || entries === null) {
    return (
      <PageShell>
        <HeroSkeleton />
        <ListSkeleton />
      </PageShell>
    );
  }

  if (entries.length === 0) {
    return (
      <PageShell>
        <Hero totalSen={0} accruedSen={0} entryCount={0} />
        <EmptyState
          title={t.t('agent.earnings.emptyTitle')}
          body={t.t('agent.earnings.emptyBody')}
        />
      </PageShell>
    );
  }

  // ── populated ───────────────────────────────────────────────────────
  // MONEY ACCURACY: all sums live in the shared, tested helpers; sen stay
  // integers here and `money.format` performs the single divide-by-100.
  const nowMs = Date.now();
  const filtered = filterLedgerByStatus(filterLedgerByRange(entries, range, nowMs), status);
  const totals = summariseLedgerTotals(filtered);

  return (
    <PageShell>
      <Hero totalSen={totals.net} accruedSen={totals.accrued} entryCount={filtered.length} />
      <EarningsControls
        range={range}
        status={status}
        onRange={setRange}
        onStatus={setStatus}
        onExport={() =>
          downloadCsv(
            `kilat-agent-earnings-${csvDateStamp(nowMs)}.csv`,
            toEarningsStatementCsv(filtered, earningsCsvLabels(t)),
          )
        }
        exportDisabled={filtered.length === 0}
      />
      {filtered.length === 0 ? (
        <EmptyState
          title={t.t('agent.earnings.filteredEmptyTitle')}
          body={t.t('agent.earnings.filteredEmptyBody')}
        />
      ) : (
        <LedgerGroups entries={filtered} />
      )}
    </PageShell>
  );
}

// ── shell ───────────────────────────────────────────────────────────────

function PageShell({ children }: { readonly children: React.ReactNode }) {
  return (
    <main
      className="ag-page"
      style={{
        minHeight: '100dvh',
        background: 'var(--tint-subtle)',
        padding: 'var(--space-4) var(--space-4) var(--space-12) var(--space-4)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-4)',
        margin: '0 auto',
      }}
    >
      {children}
    </main>
  );
}

// ── hero ────────────────────────────────────────────────────────────────

function Hero({
  totalSen,
  accruedSen,
  entryCount,
}: {
  readonly totalSen: number;
  readonly accruedSen: number;
  readonly entryCount: number;
}) {
  const t = useI18n();
  return (
    <section
      style={{
        background: 'var(--color-primary)',
        color: 'var(--color-primary-fg)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-6)',
        boxShadow: 'var(--shadow-2)',
      }}
    >
      <p
        style={{
          margin: 0,
          fontSize: 'var(--text-meta)',
          opacity: 0.8,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
        }}
      >
        {t.t('agent.earnings.netEarnings')}
      </p>
      <p
        style={{
          margin: 'var(--space-2) 0 0 0',
          fontSize: 'var(--text-display)',
          fontWeight: 600,
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {money.format(totalSen)}
      </p>
      <div
        style={{
          marginTop: 'var(--space-4)',
          display: 'flex',
          gap: 'var(--space-6)',
          flexWrap: 'wrap',
          fontSize: 'var(--text-meta)',
          opacity: 0.92,
        }}
      >
        <span>
          {t.t('agent.earnings.accrued')}{' '}
          <strong style={{ fontVariantNumeric: 'tabular-nums' }}>{money.format(accruedSen)}</strong>
        </span>
        <span>
          {t.t('agent.earnings.entries')} <strong>{entryCount}</strong>
        </span>
      </div>
    </section>
  );
}

function HeroSkeleton() {
  const t = useI18n();
  return (
    <section
      style={{
        background: 'var(--color-border)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-6)',
        height: 168,
      }}
      aria-busy="true"
      aria-label={t.t('agent.earnings.loadingAria')}
    />
  );
}

// ── ledger groups ───────────────────────────────────────────────────────

function LedgerGroups({ entries }: { readonly entries: readonly EarningsLedgerEntry[] }) {
  const groups = useMemo(() => groupByMonth(entries), [entries]);
  return (
    <div
      className="ag-grid"
      style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}
    >
      {groups.map((g) => (
        <section
          key={g.monthKey}
          style={{
            background: 'var(--color-surface)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--space-4)',
            boxShadow: 'var(--shadow-1)',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              marginBottom: 'var(--space-3)',
            }}
          >
            <h2 style={{ margin: 0, fontSize: 'var(--text-body)', fontWeight: 600 }}>{g.label}</h2>
            <span
              style={{
                fontSize: 'var(--text-meta)',
                color: 'var(--color-on-surface-muted)',
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {money.format(g.subtotalSen)} · {g.entries.length}
            </span>
          </div>
          <ul
            style={{
              listStyle: 'none',
              margin: 0,
              padding: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--space-2)',
            }}
          >
            {g.entries.map((e) => (
              <LedgerRow key={e.id} entry={e} />
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

function LedgerRow({ entry }: { readonly entry: EarningsLedgerEntry }) {
  const basis = entry.basis as Record<string, unknown>;
  const operatorName =
    typeof basis['tenantBusinessName'] === 'string' && basis['tenantBusinessName'].length > 0
      ? (basis['tenantBusinessName'] as string)
      : entry.tenantId;
  const perKind = (basis['perKindSen'] as Record<string, unknown> | undefined) ?? {};
  const subscriptionTotalSen = numOrZero(basis['subscriptionTotalSen']);
  return (
    <li
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        gap: 'var(--space-2)',
        padding: 'var(--space-3)',
        borderRadius: 'var(--radius-md)',
        background: 'var(--tint-subtle)',
        alignItems: 'start',
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          <KindPill entry={entry} />
          <span
            style={{
              fontWeight: 500,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {operatorName}
          </span>
        </div>
        <p
          style={{
            margin: 'var(--space-1) 0 0 0',
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
            fontVariantNumeric: 'tabular-nums',
          }}
        >
          {entry.invoiceId ? `Invoice ${entry.invoiceId} · ` : ''}
          {formatDateTime(tsMs(entry.accruedAt))}
        </p>
        <BreakdownLine
          perKindSen={perKind}
          subscriptionTotalSen={subscriptionTotalSen}
          rate={entry.rate}
          multiplier={entry.multiplier}
        />
      </div>
      <div
        style={{
          textAlign: 'right',
          fontVariantNumeric: 'tabular-nums',
          fontWeight: 600,
          color: entry.amount < 0 ? '#b45309' : 'var(--color-on-surface)',
        }}
      >
        {entry.amount > 0 ? '+' : ''}
        {money.format(entry.amount)}
      </div>
    </li>
  );
}

function KindPill({ entry }: { readonly entry: EarningsLedgerEntry }) {
  // Classify via basis.shareKind so each row names the money it actually is,
  // rather than showing two indistinguishable "Accrued" pills.
  //
  // The note here used to describe a "Monthly recurring" pill. Sprint 95
  // replaced that model: an agent earns one month of subscription per signup,
  // once, and a recruiter a flat sum per downstream signup, once. No pill says
  // "recurring" any more and none should — see the labels above.
  const t = useI18n();
  const cls = classifyLedgerEntry(entry);
  const visual = pillVisualForClass(cls, t);
  return (
    <span
      style={{
        padding: '2px 8px',
        borderRadius: 'var(--radius-pill)',
        background: visual.bg,
        color: visual.fg,
        fontSize: '0.6875rem',
        fontWeight: 600,
        letterSpacing: '0.02em',
        whiteSpace: 'nowrap',
      }}
    >
      {visual.label}
    </span>
  );
}

/**
 * Colour + wording for a ledger row's class chip.
 *
 * The labels were English literals until 2026-08-25. Agents are recruited
 * across Malaysia and the app ships four locales, so "Clawback" — a word that
 * decides whether an agent thinks they have been paid or charged — was
 * readable in exactly one of them.
 */
function pillVisualForClass(
  cls: LedgerEntryClass,
  t: ReturnType<typeof useI18n>,
): { bg: string; fg: string; label: string } {
  switch (cls) {
    case 'signup':
      return {
        bg: 'rgba(13,122,79,0.12)',
        fg: 'var(--color-primary)',
        label: t.t('agent.earnings.class.signup'),
      };
    case 'first_invoice_bonus':
      return {
        bg: 'rgba(20,184,122,0.16)',
        fg: 'var(--color-primary)',
        label: t.t('agent.earnings.class.first_invoice_bonus'),
      };
    case 'recruiter_signup':
      return {
        bg: 'rgba(245,158,11,0.16)',
        fg: '#b45309',
        label: t.t('agent.earnings.class.recruiter_signup'),
      };
    case 'recruiter_bonus':
      return {
        bg: 'rgba(245,158,11,0.16)',
        fg: '#b45309',
        label: t.t('agent.earnings.class.recruiter_bonus'),
      };
    case 'payout':
      return {
        bg: 'var(--color-border)',
        fg: 'var(--color-on-surface-muted)',
        label: t.t('agent.earnings.class.payout'),
      };
    case 'clawback':
      return {
        bg: 'rgba(239,68,68,0.12)',
        fg: '#b45309',
        label: t.t('agent.earnings.class.clawback'),
      };
    case 'adjustment':
      return {
        bg: 'rgba(59,130,246,0.12)',
        fg: '#1d4ed8',
        label: t.t('agent.earnings.class.adjustment'),
      };
    case 'other':
      return {
        bg: 'var(--color-border)',
        fg: 'var(--color-on-surface-muted)',
        label: t.t('agent.earnings.class.other'),
      };
  }
}

function BreakdownLine({
  perKindSen,
  subscriptionTotalSen,
  rate,
  multiplier,
}: {
  readonly perKindSen: Record<string, unknown>;
  readonly subscriptionTotalSen: number;
  readonly rate: number;
  readonly multiplier: number;
}) {
  const parts: string[] = [];
  // Sprint 95 — agent signup commission carries `subscriptionTotalSen`
  // + `rate` (the multiplier on subscription sum). Show "1 month of
  // RM X subscription" so the agent sees where the number came from.
  if (subscriptionTotalSen > 0) {
    const monthsLabel = rate === 1 ? '1 month' : `${rate.toFixed(2)}×`;
    parts.push(`${monthsLabel} of ${money.format(subscriptionTotalSen)} subscription`);
  }
  // First-invoice fee splits keep their per-kind breakdown.
  const signupFee = numOrZero(perKindSen['signup_fee']);
  const activationFee = numOrZero(perKindSen['activation_fee']);
  if (signupFee > 0) parts.push(`Signup fee share ${money.format(signupFee)}`);
  if (activationFee > 0) parts.push(`Activation share ${money.format(activationFee)}`);
  if (parts.length === 0) return null;
  return (
    <p
      style={{
        margin: 'var(--space-1) 0 0 0',
        fontSize: 'var(--text-meta)',
        color: 'var(--color-on-surface-muted)',
      }}
    >
      {parts.join(' · ')}
      {multiplier !== 1 ? ` · ×${multiplier.toFixed(2)} tier` : ''}
    </p>
  );
}

// ── empty / cta state ───────────────────────────────────────────────────

function EmptyState({
  title,
  body,
  ctaLabel,
  ctaHref,
  ctaNote,
  onRetry,
  retryLabel,
}: {
  readonly title: string;
  readonly body: string;
  readonly ctaLabel?: string;
  readonly ctaHref?: string;
  readonly ctaNote?: string;
  readonly onRetry?: () => void;
  readonly retryLabel?: string;
}) {
  return (
    <section
      style={{
        background: 'var(--color-surface)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-6)',
        textAlign: 'center',
        boxShadow: 'var(--shadow-1)',
      }}
    >
      <h2 style={{ margin: 0, fontSize: 'var(--text-h2)', fontWeight: 600 }}>{title}</h2>
      <p
        style={{
          margin: 'var(--space-3) auto 0 auto',
          maxWidth: 420,
          fontSize: 'var(--text-meta)',
          color: 'var(--color-on-surface-muted)',
          lineHeight: 1.5,
        }}
      >
        {body}
      </p>
      {onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          style={{
            display: 'inline-block',
            marginTop: 'var(--space-4)',
            minHeight: 'var(--touch-target, 44px)',
            padding: 'var(--space-3) var(--space-6)',
            borderRadius: 'var(--radius-pill)',
            background: 'var(--color-primary)',
            color: 'var(--color-primary-fg)',
            border: 'none',
            fontWeight: 500,
            fontSize: 'var(--text-meta)',
            cursor: 'pointer',
          }}
        >
          {retryLabel}
        </button>
      ) : null}
      {ctaLabel && ctaHref ? (
        <a
          href={ctaHref}
          style={{
            display: 'inline-block',
            marginTop: 'var(--space-4)',
            padding: 'var(--space-3) var(--space-6)',
            borderRadius: 'var(--radius-pill)',
            background: 'var(--color-primary)',
            color: 'var(--color-primary-fg)',
            textDecoration: 'none',
            fontWeight: 500,
            fontSize: 'var(--text-meta)',
          }}
        >
          {ctaLabel}
        </a>
      ) : null}
      {ctaNote ? (
        <p
          style={{
            margin: 'var(--space-2) 0 0 0',
            fontSize: 'var(--text-meta)',
            color: 'var(--color-on-surface-muted)',
          }}
        >
          {ctaNote}
        </p>
      ) : null}
    </section>
  );
}

function ListSkeleton() {
  return (
    <div
      style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}
      aria-busy="true"
    >
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          style={{
            height: 76,
            borderRadius: 'var(--radius-md)',
            background: 'var(--color-border)',
          }}
        />
      ))}
    </div>
  );
}

// ── helpers ─────────────────────────────────────────────────────────────

interface MonthGroup {
  readonly monthKey: string;
  readonly label: string;
  readonly entries: EarningsLedgerEntry[];
  readonly subtotalSen: number;
}

function groupByMonth(entries: readonly EarningsLedgerEntry[]): MonthGroup[] {
  const buckets = new Map<string, EarningsLedgerEntry[]>();
  for (const e of entries) {
    const d = new Date(tsMs(e.accruedAt));
    const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    const arr = buckets.get(key) ?? [];
    arr.push(e);
    buckets.set(key, arr);
  }
  return Array.from(buckets.entries())
    .sort((a, b) => (a[0] < b[0] ? 1 : -1))
    .map(([monthKey, group]) => {
      group.sort((a, b) => tsMs(b.accruedAt) - tsMs(a.accruedAt));
      const subtotalSen = group.reduce((acc, e) => acc + e.amount, 0);
      const first = group[0];
      const sample = first ? new Date(tsMs(first.accruedAt)) : new Date();
      const label = sample.toLocaleDateString(undefined, { month: 'long', year: 'numeric' });
      return { monthKey, label, entries: group, subtotalSen };
    });
}

function tsMs(ts: { toMillis?: () => number; seconds?: number } | unknown): number {
  if (!ts || typeof ts !== 'object') return 0;
  const obj = ts as { toMillis?: () => number; seconds?: number; nanoseconds?: number };
  if (typeof obj.toMillis === 'function') return obj.toMillis();
  if (typeof obj.seconds === 'number') {
    return obj.seconds * 1000 + Math.floor((obj.nanoseconds ?? 0) / 1_000_000);
  }
  return 0;
}

function formatDateTime(ms: number): string {
  if (!ms) return '—';
  const d = new Date(ms);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
}

function numOrZero(v: unknown): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : 0;
}
